import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import {
    Save, ArrowLeft, Plus, ChevronRight, Search,
    Trash2, ShieldAlert, Zap, Info, Tag, Paperclip, History
} from 'lucide-react';

// المودالات الموحدة من نظام الـ PO
import UniversalLookup from './UniversalLookup';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import StatusHistoryModal from './StatusHistoryModal';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

const HazardDetails = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = !id || id === 'new';
    
    const [activeTab, setActiveTab] = useState('Hazard');
    const [loading, setLoading] = useState(false);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showAudit, setShowAudit] = useState(false);
    const [count, setCount] = useState(0);

    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const [data, setData] = useState({
        hazardnum: '', description: '', hazardtype: '', priority: 'MEDIUM',
        siteid: session.site_id || '', orgid: session.org_id || '',
        precautions: [],
        tagouts: [] // المخاطر بتربط بنقاط عزل برضه في Setup الأمان
    });

    const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', targetIdx: null });

    // 1. جلب البيانات
    useEffect(() => {
        if (!isNew) {
            axios.get(`/api/hazards/${id}`).then(res => {
                const d = res.data;
                d.precautions = d.precautions || [];
                d.tagouts = d.tagouts || [];
                setData(d);
            });
        }
    }, [id, isNew]);

    // 2. جلب عدد المرفقات
    const fetchCount = useCallback(async () => {
        if (!data.hazardnum || isNew) { setCount(0); return; }
        try {
            const res = await axios.get(`/api/attachments/count/HAZARD/${data.hazardnum}`);
            setCount(res.data.count || 0);
        } catch (err) { console.error(err); }
    }, [data.hazardnum, isNew]);

    useEffect(() => { fetchCount(); }, [fetchCount]);

    useEffect(() => {
        const openLog = () => setShowAudit(true);
        window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
        return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
    }, []);

    // --- الدوال الأساسية ---
    const handleSave = async () => {
        try {
            setLoading(true);
            await axios.post('/api/hazards/save', data);
            toast.success(t('hazards.details.saveSuccess', 'Hazard Master Saved!'));
            if (isNew) navigate(`/hazards/${data.hazardnum}`);
        } catch (err) { toast.error(t('common.save_fail', 'Save failed')); }
        finally { setLoading(false); }
    };

    const handleDelete = () => {
        Swal.fire({
            ...swalTheme(),
            title: t('common.are_you_sure', 'Are you sure?'), text: t('hazards.details.deleteConfirmText', 'This will delete the hazard permanently!'),
            icon: 'warning', showCancelButton: true, confirmButtonColor: swalDangerColor(),
            confirmButtonText: t('common.yes_delete', 'Yes, delete it!')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`/api/hazards/${data.hazardnum}`);
                    toast.success(t('hazards.details.deleteSuccess', 'Hazard deleted!'));
                    navigate('/hazards');
                } catch (err) { toast.error(t('common.delete_fail', 'Delete failed')); }
            }
        });
    };


    const handleLookupSelect = (row) => {
        const newData = { ...data };
        if (lookup.field === 'precautionnum') {
            newData.precautions[lookup.targetIdx].precautionnum = row.precautionnum;
            newData.precautions[lookup.targetIdx].description = row.description;
        } else if (lookup.field === 'tagoutnum') {
            newData.tagouts[lookup.targetIdx].tagoutnum = row.tagoutnum;
            newData.tagouts[lookup.targetIdx].description = row.description;
        } else {
            newData[lookup.field] = row.value || row.site_id || row.org_id;
        }
        setData(newData);
        setLookup({ ...lookup, isOpen: false });
    };
    // --- Global Handlers for Save/Delete (تقدر تشغلهم من أي مكان في التطبيق) ---  
    useEffect(() => {
        const triggerSave = () => handleSave();
        window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
        return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    }, [handleSave]);
    
    useEffect(() => {
        const triggerDelete = () => handleDelete();
        window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
        return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    }, [handleDelete]);
    return (
        <div className="flex flex-col h-screen bg-surface-app text-[11px] font-sans uppercase">
            <UniversalLookup {...lookup} onClose={() => setLookup({ ...lookup, isOpen: false })} onSelect={handleLookupSelect} />
            {!isNew && <AttachmentsModal isOpen={isDocModalOpen} onClose={() => { setIsDocModalOpen(false); fetchCount(); }} ownertable="HAZARD" ownerid={data.hazardnum} />}
            {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="HAZARD" recordId={data.hazardnum} onClose={() => setShowHistoryModal(false)} />}
            <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="hazards" recordId={data.hazardnum} />

            {/* Header Bar - PO DNA */}
            <div className="bg-surface-card p-2 border-b flex justify-between items-center shadow-sm z-20 font-bold tracking-tighter">
                <div className="flex gap-6 ml-4">
                    <button onClick={() => navigate('/hazards')} className="p-1 hover:bg-surface-panel rounded-full text-primary"><ArrowLeft size={18} /></button>
                    <HeaderField label={t('hazards.fields.hazardNum', 'Hazard Num')} value={data.hazardnum} isBold width="w-32" disabled={!isNew} onChange={v => setData({ ...data, hazardnum: v })} />
                    <HeaderField label={t('hazards.fields.description', 'Description')} value={data.description} onChange={v => setData({ ...data, description: v })} width="w-[300px]" />
                    <HeaderField label={t('common.site', 'Site')} value={data.siteid} disabled width="w-24" />
                </div>
                <div className="flex gap-2 mr-4">
                    <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-primary rounded-sm hover:bg-primary hover:text-white transition-all">
                        <Paperclip size={18} />
                        {count > 0 && <span className="absolute -top-1 -right-1 bg-status-danger text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center border border-white">{count}</span>}
                    </button>
                    <button onClick={() => setShowHistoryModal(true)} className="p-2 text-primary hover:bg-primary-soft rounded"><History size={16} /></button>
                    <button onClick={handleSave} disabled={loading} className="bg-primary text-white px-8 py-2 rounded font-black flex items-center gap-2 hover:bg-primary-dark shadow-lg">
                        <Save size={14} /> {loading ? t('common.saving', 'SAVING...') : t('common.save', 'SAVE')}
                    </button>
                </div>
            </div>

            {/* Navigation Tabs - Blue Theme */}
            <div className="flex bg-primary-dark text-white">
                {[
                    { id: 'Hazard', label: t('hazards.details.tabHazard', 'Hazard Info'), icon: <ShieldAlert size={14} /> },
                    { id: 'Precautions', label: t('hazards.details.tabPrecautions', 'Default Precautions'), icon: <Tag size={14} /> },
                    { id: 'Tagouts', label: t('hazards.details.tabTagouts', 'Linked Tag Outs'), icon: <Zap size={14} /> }
                ].map(tb => (
                    <button key={tb.id} onClick={() => setActiveTab(tb.id)} className={`px-10 py-3 flex items-center gap-2 font-black border-r border-white/5 ${activeTab === tb.id ? 'bg-surface-card text-primary-dark' : 'opacity-75 hover:opacity-100'}`}>
                        {tb.icon} {tb.label}
                    </button>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4 bg-surface-panel">
                {/* --- Tab 1: Hazard Info --- */}
                {activeTab === 'Hazard' && (
                    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
                        <SectionCard title={t('hazards.details.hazardDefinition', 'Hazard Definition')}>
                            <div className="grid grid-cols-2 gap-8 p-2">
                                <EditableField label={t('hazards.fields.hazardNumber', 'Hazard Number')} value={data.hazardnum} disabled={!isNew} />
                                <EditableField label={t('hazards.fields.hazardType', 'Hazard Type')} value={data.hazardtype}
                                    onSearch={() => setLookup({
                                    isOpen: true,
                                    title: t('hazards.lookup.selectHazardType', 'Select Hazard Type'),
                                    type: 'DOMAIN',
                                    domainId: 'HAZARDTYPE', // <--- ابعتها كـ Prop صريح هنا
                                    field: 'hazardtype',
                                    filters: { domainid: 'HAZARDTYPE' }
                                  })}
                                />
                                <EditableField label={t('common.org', 'Organization')} value={data.orgid} disabled />
                                <EditableField label={t('hazards.fields.priority', 'Priority')} value={data.priority} onChange={v => setData({...data, priority: v})} />
                                <div className="col-span-2">
                                    <EditableField label={t('hazards.fields.fullDescription', 'Full Description')} value={data.description} onChange={v => setData({ ...data, description: v })} />
                                </div>
                            </div>
                        </SectionCard>
                    </div>
                )}

                {/* --- Tab 2: Default Precautions --- */}
                {activeTab === 'Precautions' && (
                    <div className="max-w-6xl mx-auto animate-in slide-in-from-bottom duration-500">
                        <SectionCard title={t('hazards.details.precautionsForHazard', 'Predefined Precautions for this Hazard')}>
                            <div className="bg-surface-card border border-line rounded overflow-hidden">
                                <table className="w-full text-left">
                                    <thead className="bg-surface-panel border-b border-line text-ink-secondary font-bold uppercase text-[9px]">
                                        <tr>
                                            <th className="p-2 border-r border-line w-40">{t('precautions.fields.precautionNum', 'Precaution Num')}</th>
                                            <th className="p-2 border-r border-line">{t('hazards.details.instructionDesc', 'Instruction / Description')}</th>
                                            <th className="p-2 text-center w-12">{t('common.action', 'Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.precautions.map((p, i) => (
                                            <tr key={i} className="border-b border-line-subtle hover:bg-primary-soft">
                                                <td className="p-2 border-r border-line-subtle font-black text-primary group">
                                                    <div className="flex justify-between items-center px-1">
                                                        {p.precautionnum || '---'}
                                                        <Search size={12} className="text-primary cursor-pointer"
                                                            onClick={() => setLookup({ isOpen: true, title: t('hazards.lookup.selectPrecaution', 'Select Precaution'), type: 'precautions', field: 'precautionnum', targetIdx: i })} />
                                                    </div>
                                                </td>
                                                <td className="p-2 border-r border-line-subtle font-semibold text-ink uppercase">{p.description}</td>
                                                <td className="p-2 text-center">
                                                    <Trash2 size={12} className="text-status-danger hover:opacity-70 cursor-pointer mx-auto"
                                                        onClick={() => {
                                                            const np = data.precautions.filter((_, idx) => idx !== i);
                                                            setData({...data, precautions: np});
                                                        }} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button onClick={() => setData({...data, precautions: [...data.precautions, {precautionnum:'', description:''}]})}
                                    className="m-2 bg-surface-panel text-ink hover:bg-primary-dark hover:text-white px-4 py-1 rounded-sm font-black flex items-center gap-2 transition-all">
                                    <Plus size={12} /> {t('hazards.details.addPrecautionLine', 'ADD PRECAUTION LINE')}
                                </button>
                            </div>
                        </SectionCard>
                    </div>
                )}

                {/* --- Tab 3: Linked Tag Outs --- */}
                {activeTab === 'Tagouts' && (
                    <div className="max-w-6xl mx-auto animate-in slide-in-from-bottom duration-500">
                        <SectionCard title={t('hazards.details.isolationPoints', 'Required Isolation Points (LOTO Master)')}>
                            <div className="bg-surface-card border border-line rounded overflow-hidden">
                                <table className="w-full text-left">
                                    <thead className="bg-surface-panel border-b border-line text-ink-secondary font-bold uppercase text-[9px]">
                                        <tr>
                                            <th className="p-2 border-r border-line w-40">{t('tagouts.fields.tagOutNum', 'Tag Out Num')}</th>
                                            <th className="p-2 border-r border-line">{t('tagouts.fields.isolationDescription', 'Isolation Description')}</th>
                                            <th className="p-2 text-center w-12">{t('common.action', 'Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.tagouts.map((tg, i) => (
                                            <tr key={i} className="border-b border-line-subtle hover:bg-primary-soft">
                                                <td className="p-2 border-r border-line-subtle font-black text-primary px-3">
                                                    <div className="flex justify-between items-center">
                                                        {tg.tagoutnum || '---'}
                                                        <Search size={12} className="text-primary cursor-pointer"
                                                            onClick={() => setLookup({ isOpen: true, title: t('hazards.lookup.selectTagOut', 'Select TagOut'), type: 'tagouts', field: 'tagoutnum', targetIdx: i })} />
                                                    </div>
                                                </td>
                                                <td className="p-2 border-r border-line-subtle font-semibold text-ink-secondary">{tg.description}</td>
                                                <td className="p-2 text-center">
                                                    <Trash2 size={12} className="text-status-danger hover:opacity-70 cursor-pointer mx-auto"
                                                        onClick={() => {
                                                            const nt = data.tagouts.filter((_, idx) => idx !== i);
                                                            setData({...data, tagouts: nt});
                                                        }} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button onClick={() => setData({...data, tagouts: [...data.tagouts, {tagoutnum:'', description:''}]})}
                                    className="m-2 bg-surface-panel text-ink hover:bg-primary-dark hover:text-white px-4 py-1 rounded-sm font-black flex items-center gap-2 transition-all">
                                    <Plus size={12} /> {t('hazards.details.addTagoutLine', 'ADD TAG OUT LINE')}
                                </button>
                            </div>
                        </SectionCard>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Reusable Components (نفس الستايل بتاعك بالظبط) ---

const HeaderField = ({ label, value, onChange, width, isBold, disabled }) => (
    <div className="flex flex-col border-r border-line px-4 last:border-0">
        <label className="text-[8px] text-ink-secondary font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
        <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-primary-dark text-[12px]' : 'text-primary'} bg-transparent focus:border-primary`}
            value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
    </div>
);

const SectionCard = ({ title, children }) => (
    <div className="bg-surface-card border border-line rounded shadow-sm overflow-hidden transition-all hover:shadow-md">
        <div className="p-2 bg-surface-panel border-b border-line font-black text-ink-secondary uppercase text-[9px] tracking-widest flex items-center gap-2">
            <div className="w-1.5 h-3 bg-primary rounded-full"></div> {title}
        </div>
        <div className="p-5">{children}</div>
    </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled }) => (
    <div className="flex flex-col gap-1 border-b border-line group focus-within:border-primary transition-all py-1">
        <label className="text-ink-secondary font-black text-[8px] uppercase tracking-widest italic">{label}</label>
        <div className="flex items-center justify-between pb-0.5">
            <input type={type} disabled={disabled} className="outline-none font-black text-primary bg-transparent w-full text-[11px]"
                value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
            {onSearch && <Search size={12} className="text-primary cursor-pointer hover:text-primary-dark transition-colors" onClick={onSearch} />}
        </div>
    </div>
);

export default HazardDetails;